import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import createOrderApi, { ApproveParams, ApproveParamsItem, SortKey, AdditionalApproveParams } from 'api/order';
import { appContextSelector } from 'modules/app/appSlice';
import { showToast } from 'modules/app/toasterSlice';
import { syncAttribution } from 'modules/user/attributionSlice';
import { currentUserSelector } from 'modules/user/userSlice';
import { AppThunk, RootState } from 'store';
import { setTag, tags } from 'utils/tags';
import { roundNumberToTwo } from 'utils/numberFormatter';
import { mapTransactionProducts } from 'utils/order';

interface SetAllOrderParams {
  orders: Order[];
}

interface SetOrderParams<T> {
  orders: T[];
  total: number;
}

interface State {
  allOrders: Order[];
  orders: Order[];
  isLoading: boolean;
  error?: string;
  totalItems: number;
  facilitatedOrder?: Order;
  viewedOrder?: Order;
  corporateOrders: {
    [key: string]: { total: number; orders: Order[] };
  };
  approveParamsItems?: ApproveParamsItem[];
  updatedFacilitatedOrderPrice?: number;
  updatedFacilitatedOrderVat?: number;
  updatedFacilitatedOrderExcise?: number;
  updatedFacilitatedOrderTotalKilosCo2Avoided?: number;
  updatedFacilitatedOrderTotalKilosCo2Offset?: number;
  isFetchingFacilitatedOrderData: boolean;
  multiplier?: Multiplier;
  facilitatedOrderFootprint?: Footprint[];
  additionalApproveParams?: AdditionalApproveParams;
  facilitatedOrdersForSingleCustomers: SetOrderParams<Order>;
  facilitatedOrdersForMultipleCustomers: SetOrderParams<MultiCustomersOrder>;
}

export interface OrderHistoryFilters {
  type?: string;
  from?: string;
  to?: string;
  department?: string;
  country?: string;
  page?: number;
  paymentStatus?: string;
}

export interface FacilitatedOrderHistoryFilters {
  page?: number;
}

export const paymentStatusFilters = ['Draft', 'Open', 'Paid', 'Uncollectible', 'Void', 'Proposed'];

export const LIMIT = 10;

const initialState: State = {
  allOrders: [],
  orders: [],
  isLoading: false,
  totalItems: 0,
  corporateOrders: {},
  isFetchingFacilitatedOrderData: false,
  facilitatedOrdersForSingleCustomers: {
    orders: [],
    total: 0,
  },
  facilitatedOrdersForMultipleCustomers: {
    orders: [],
    total: 0,
  },
};

const slice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setAllOrders: (state: State, action: PayloadAction<SetAllOrderParams>): State => {
      state.allOrders = action.payload.orders;
      return state;
    },
    setOrders: (state: State, action: PayloadAction<SetOrderParams<Order>>): State => {
      state.orders = action.payload.orders;
      state.totalItems = action.payload.total;
      return state;
    },
    setFacilitatedOrder: (state: State, action: PayloadAction<Order>): State => {
      state.facilitatedOrder = action.payload;
      return state;
    },
    setViewedOrder: (state: State, action: PayloadAction<Order>): State => {
      state.viewedOrder = action.payload;
      return state;
    },
    resetViewedOrder: (state: State): State => {
      state.viewedOrder = undefined;
      return state;
    },
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.isLoading = action.payload;
      return state;
    },
    setIsFetchingFacilitatedOrderData: (state: State, action: PayloadAction<boolean>): State => {
      state.isFetchingFacilitatedOrderData = action.payload;
      return state;
    },
    setError: (state: State, action: PayloadAction<string>): State => {
      state.error = action.payload;
      return state;
    },
    appendOrder: (state: State, action: PayloadAction<Order>): State => {
      state.orders.unshift(action.payload);
      if (state.orders.length > 10) {
        state.orders.pop();
      }
      return state;
    },
    setCorporateOrderItem: (state: State, action: PayloadAction<{ key: string; orders: SetOrderParams<Order> }>): State => {
      state.corporateOrders[action.payload.key] = action.payload.orders;
      return state;
    },
    setApproveParamItems: (state: State, action: PayloadAction<ApproveParamsItem[]>): State => {
      state.approveParamsItems = action.payload;
      return state;
    },
    setUpdatedFacilitatedOrderPrice: (state: State, action: PayloadAction<number>): State => {
      state.updatedFacilitatedOrderPrice = action.payload;
      return state;
    },
    setUpdatedFacilitatedOrderVat: (state: State, action: PayloadAction<number>): State => {
      state.updatedFacilitatedOrderVat = action.payload;
      return state;
    },
    setUpdatedFacilitatedOrderExcise: (state: State, action: PayloadAction<number>): State => {
      state.updatedFacilitatedOrderExcise = action.payload;
      return state;
    },
    setUpdatedFacilitatedOrderTotalKilosCo2Avoided: (state: State, action: PayloadAction<number>): State => {
      state.updatedFacilitatedOrderTotalKilosCo2Avoided = action.payload;
      return state;
    },
    setUpdatedFacilitatedOrderTotalKilosCo2Offset: (state: State, action: PayloadAction<number>): State => {
      state.updatedFacilitatedOrderTotalKilosCo2Offset = action.payload;
      return state;
    },
    setMultiplier: (state: State, action: PayloadAction<Multiplier>): State => {
      state.multiplier = action.payload;
      return state;
    },
    setFacilitatedOrderFootprint: (state: State, action: PayloadAction<Footprint[]>): State => {
      state.facilitatedOrderFootprint = action.payload;
      return state;
    },
    setAdditionalApproveParams: (state: State, action: PayloadAction<AdditionalApproveParams>): State => {
      state.additionalApproveParams = action.payload;
      return state;
    },
    resetAdditionalApproveParams: (state: State): State => {
      state.additionalApproveParams = undefined;
      return state;
    },
    setFacilitatedOrdersForSingleCustomer: (state: State, action: PayloadAction<SetOrderParams<Order>>): State => {
      state.facilitatedOrdersForSingleCustomers = action.payload;
      return state;
    },
    setFacilitatedOrdersForMultipleCustomers: (state: State, action: PayloadAction<SetOrderParams<MultiCustomersOrder>>): State => {
      state.facilitatedOrdersForMultipleCustomers = action.payload;
      return state;
    },
  },
});
const {
  setOrders,
  setError,
  setLoading,
  appendOrder,
  setAllOrders,
  setFacilitatedOrder,
  setViewedOrder,
  resetViewedOrder,
  setCorporateOrderItem,
  setApproveParamItems,
  setUpdatedFacilitatedOrderPrice,
  setUpdatedFacilitatedOrderVat,
  setUpdatedFacilitatedOrderExcise,
  setUpdatedFacilitatedOrderTotalKilosCo2Avoided,
  setUpdatedFacilitatedOrderTotalKilosCo2Offset,
  setIsFetchingFacilitatedOrderData,
  setMultiplier,
  setFacilitatedOrderFootprint,
  setAdditionalApproveParams,
  resetAdditionalApproveParams,
  setFacilitatedOrdersForSingleCustomer,
  setFacilitatedOrdersForMultipleCustomers,
} = slice.actions;
export { appendOrder, setMultiplier, resetViewedOrder, setAdditionalApproveParams, resetAdditionalApproveParams };
export default slice.reducer;

type OrderHistoryTab = 'pendingConfirmation' | 'pendingPayment' | 'paid';

export const getOrderHistory = (filters?: OrderHistoryFilters, sortKey?: SortKey, tab?: OrderHistoryTab): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const orderApi = createOrderApi(context);

  try {
    const excludedStatuses = filters?.paymentStatus ? paymentStatusFilters.filter(f => !filters.paymentStatus?.includes(f)).join(',') : undefined;

    const appliedFilters = {
      category: filters?.type,
      fromCreatedDate: filters?.from,
      toCreatedDate: filters?.to,
      'metadata.custom.department': filters?.department,
      'metadata.custom.country': filters?.country,
      'payment.status': excludedStatuses,
    };

    const currentPage = filters && filters.page ? filters.page - 1 : 0;

    const result = await orderApi.getAll({ offset: currentPage * LIMIT, limit: LIMIT, ...appliedFilters, sortKey }, currentUser?.customerId ?? '');

    if (tab) {
      dispatch(setCorporateOrderItem({ key: tab, orders: { orders: result.items, total: result.totalCount } }));
    } else {
      dispatch(
        setOrders({
          orders: result.items,
          total: result.totalCount,
        }),
      );
    }

    dispatch(setLoading(false));
  } catch (err) {
    // @ts-ignore
    dispatch(setError(err?.error ? err.error : err?.message));
    dispatch(setLoading(false));
  }
};

export const getAllOrders = (): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const orderApi = createOrderApi(context);
  try {
    const result = await orderApi.getAll({ offset: 0 * LIMIT, limit: LIMIT }, currentUser?.customerId ?? '');
    dispatch(
      setAllOrders({
        orders: result.items,
      }),
    );
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
  }
};

export const getFacilitatedOrderData = (customerId: string, orderId: string, errorCb?: CallableFunction, retries = 0): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  dispatch(setLoading(true));
  const maxNumberOfRetries = 10;
  const retriesNumber = retries;

  const context = appContextSelector(getState());
  const orderApi = createOrderApi(context);

  try {
    const order = await orderApi.getOne(orderId, customerId, {
      includeStats: true,
      includeAttachments: true,
      includeVirtualPortfolio: true,
    });
    const footprint = await orderApi.getFootprint(orderId, customerId);
    dispatch(setFacilitatedOrderFootprint(footprint));
    dispatch(setFacilitatedOrder(order));
    dispatch(setLoading(false));
  } catch (err) {
    if (maxNumberOfRetries > retriesNumber) {
      setTimeout(() => dispatch(getFacilitatedOrderData(customerId, orderId, errorCb, retriesNumber + 1)), 2000);
      dispatch(
        showToast({
          variant: 'error',
          titlei18nKey: 'offset:orderClaim.errorOrderNotFoundTitleWillRetry',
          descriptioni18nKey: 'offset:orderClaim.errorOrderNotFoundDescriptionWillRetry',
        }),
      );
    } else {
      dispatch(setLoading(false));
      dispatch(
        showToast({ variant: 'error', descriptioni18nKey: 'offset:orderClaim.errorOrderNotFoundDescription', titlei18nKey: 'common:errorTitle' }),
      );
      errorCb && errorCb();
    }
  }
};

export const getViewedOrder = (orderId: string, errorCb?: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const orderApi = createOrderApi(context);

  try {
    const order = await orderApi.getOne(orderId, currentUser?.customerId ?? '', {
      includeStats: true,
      includeAttachments: true,
      includeVirtualPortfolio: true,
    });
    const footprint = await orderApi.getFootprint(orderId, currentUser?.customerId ?? '');
    dispatch(setFacilitatedOrderFootprint(footprint));
    dispatch(setViewedOrder(order));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', descriptioni18nKey: 'common:errorDescription', titlei18nKey: 'common:errorTitle' }));
    errorCb && errorCb();
  }
};

export const getApproveParams = (percentage: number): AppThunk => async (dispatch, getState): Promise<void> => {
  const { facilitatedOrder } = getState().orders;

  const context = appContextSelector(getState());
  const customerId = getState().user.currentAccount?.customerId ?? '';
  const currency = getState().user.currentAccount?.customer?.payment?.currency;
  const { virtualPortfolios } = getState().offsetPortfolioSelect;
  const arePortfoliosRedistributed = virtualPortfolios.some(p => p.weight > 0);

  const percentageAsFraction = percentage / 100;

  try {
    dispatch(setIsFetchingFacilitatedOrderData(true));

    if (facilitatedOrder) {
      const mappedVirtualPortfolios = arePortfoliosRedistributed
        ? virtualPortfolios.filter(({ weight }) => weight > 0).map(({ id, weight }) => ({ id, weight: weight * percentageAsFraction }))
        : undefined;
      const orderApi = createOrderApi(context);
      const newItems = facilitatedOrder.items.map(item => {
        const airports = item.details?.flight?.routeLegs
          ?.map((route, index) => {
            if (index === 0) {
              return [route.fromAirportCode, route.toAirportCode];
            }

            return route.toAirportCode;
          })
          ?.flat();
        return {
          type: 'impact',
          impact: 'Co2',
          details: item.details,
          feeFactors: item.feeFactors,
          taxFactors: {
            airports,
          },
          kilosCo2: item.kilosCo2 * percentageAsFraction,
          portfolio:
            mappedVirtualPortfolios || facilitatedOrder.virtualPortfolio?.childPortfolios
              ? {
                  type: 'Virtual',
                  impact: 'co2',
                  currency: currency ?? 'USD',
                  childPortfolios: (mappedVirtualPortfolios as VirtualPortfolio[]) ?? facilitatedOrder.virtualPortfolio?.childPortfolios ?? [],
                }
              : undefined,
          portfolioId: mappedVirtualPortfolios || facilitatedOrder.virtualPortfolio?.childPortfolios ? null : facilitatedOrder.portfolioId ?? null,
        };
      });

      const order = {
        currency: currency ?? 'USD',
        category: facilitatedOrder.category,
        items: newItems,
      };

      //@ts-ignore
      const response = await orderApi.getQuoteForCustomer(order, customerId);
      const approveParamsItems = response.items.map(({ kilosCo2, price }) => ({ kilosCo2, price }));
      dispatch(setApproveParamItems(approveParamsItems));
      dispatch(setUpdatedFacilitatedOrderPrice(response.totalPrice));
      dispatch(setUpdatedFacilitatedOrderVat(response.totalVat));
      dispatch(setUpdatedFacilitatedOrderExcise(response.totalExcise));
      dispatch(setUpdatedFacilitatedOrderTotalKilosCo2Avoided(response.totalKilosCo2Avoided ?? 0));
      dispatch(setUpdatedFacilitatedOrderTotalKilosCo2Offset(response.totalKilosCo2Offset ?? 0));
    }
  } catch (err) {
    //err
  } finally {
    dispatch(setIsFetchingFacilitatedOrderData(false));
  }
};

export const approveFacilitatedOrder = (
  orderId: string,
  approveParams?: ApproveParams,
  successCb?: CallableFunction,
  errorCb?: CallableFunction,
): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));

  const context = appContextSelector(getState());
  const orderApi = createOrderApi(context);
  const customerId = getState().user.currentAccount?.customerId ?? '';

  try {
    const { order } = await orderApi.approveOrder(orderId, customerId, approveParams ?? {});
    dispatch(syncAttribution());
    const transactionProductName = mapTransactionProducts(order.category);
    setTag({
      ...tags.portalOffset.placeOrder,
      categories: [order.category],
      transactionId: order.id,
      transactionTotal: order.totalPrice,
      currencyCode: order.currency,
      transactionProducts: order.items.map(item => ({
        category: order.category,
        name: `${transactionProductName} offset`,
        price: roundNumberToTwo(item.price),
        quantity: item.kilosCo2,
        sku: `${transactionProductName.toLowerCase()}-offset`,
      })),
    });
    successCb && successCb();
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', descriptioni18nKey: 'common:errorDescription', titlei18nKey: 'common:errorTitle' }));
    errorCb && errorCb();
  }
};

export const rejectFacilitatedOrder = (orderId: string, successCb?: CallableFunction, errorCb?: CallableFunction): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  dispatch(setLoading(true));
  const customerId = getState().user.currentAccount?.customerId ?? '';

  const context = appContextSelector(getState());
  const orderApi = createOrderApi(context);

  try {
    await orderApi.rejectOrder(orderId, customerId);
    successCb && successCb();
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(showToast({ variant: 'error', descriptioni18nKey: 'common:errorDescription', titlei18nKey: 'common:errorTitle' }));
    dispatch(syncAttribution());
    errorCb && errorCb();
    dispatch(setLoading(false));
  }
};

export const getFacilitatedOrderHistoryForSingleCustomers = (filters: FacilitatedOrderHistoryFilters): AppThunk => async (dispatch, getState) => {
  dispatch(setLoading(true));
  const context = appContextSelector(getState());
  const orderApi = createOrderApi(context);
  const { currentAccount } = getState().user;
  const currentPage = filters?.page ? filters.page - 1 : 0;

  try {
    const facilitatedOrders = await orderApi.getFacilitatedOrdersForSingleCustomers(
      { offset: currentPage * LIMIT, limit: LIMIT },
      currentAccount?.partnershipId ?? '',
    );

    dispatch(
      setFacilitatedOrdersForSingleCustomer({
        orders: facilitatedOrders.items,
        total: facilitatedOrders.totalCount,
      }),
    );
  } catch (e) {
    /// err
  } finally {
    dispatch(setLoading(false));
  }
};

export const getFacilitatedOrderHistoryForMultipleCustomers = (filters: FacilitatedOrderHistoryFilters): AppThunk => async (dispatch, getState) => {
  dispatch(setLoading(true));
  const context = appContextSelector(getState());
  const orderApi = createOrderApi(context);
  const { currentAccount } = getState().user;
  const currentPage = filters?.page ? filters.page - 1 : 0;

  try {
    const multipleCustomersOrder = await orderApi.getFacilitatedOrdersForMultipleCustomers(
      { offset: currentPage * LIMIT, limit: LIMIT },
      currentAccount?.partnershipId ?? '',
    );

    dispatch(
      setFacilitatedOrdersForMultipleCustomers({
        orders: multipleCustomersOrder.items,
        total: multipleCustomersOrder.totalCount,
      }),
    );
  } catch (e) {
    // err
  } finally {
    dispatch(setLoading(false));
  }
};

export const totalFacilitatedFootprintSelector = createSelector(
  (state: RootState) => state.orders.facilitatedOrderFootprint,
  footprint => footprint?.map(item => item.footprint.co2.kilosCo2).reduce((a, b) => a + b, 0),
);
